/**
 * Renders dynamic text on the certificate PNG (`public/images/cert2.png`).
 * Layout uses ratios so it scales with template dimensions; tweak if the art moves.
 */
export const CERTIFICATE_TEMPLATE_PATH = 'images/cert2.png';

/** Alphabetic baseline Y for recipient name (script sits *below* “This is to proudly certify that”). */
const NAME_BASELINE_Y_RATIO = 0.388;
/**
 * Alphabetic baseline of first module line (between printed program title and “in recognition…”).
 * Nudge up/down if spacing to the lines above/below feels tight on cert2.png.
 * Raised after template trim (no outer border) so module lines clear the two-line program title.
 */
const MODULE_FIRST_BASELINE_Y_RATIO = 0.570;
const NAME_MAX_WIDTH_RATIO = 0.62;
const MODULE_MAX_WIDTH_RATIO = 0.82;

function drawParticipantName(ctx, canvas, participantName) {
  const maxNameWidth = canvas.width * NAME_MAX_WIDTH_RATIO;
  const baseFont = Math.round(canvas.width * 0.058);
  const minFont = Math.round(canvas.width * 0.034);
  let fontSize = baseFont;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#4b2a63';
  while (fontSize > minFont) {
    ctx.font = `400 ${fontSize}px "Great Vibes", "Allura", "Edwardian Script ITC", "Lucida Handwriting", cursive`;
    if (ctx.measureText(participantName).width <= maxNameWidth) break;
    fontSize -= 1;
  }
  const y = canvas.height * NAME_BASELINE_Y_RATIO;
  ctx.fillText(participantName, canvas.width / 2, y);
}

function wrapLines(ctx, text, fontSize, maxW) {
  ctx.font = `600 ${fontSize}px "Times New Roman", Georgia, "Noto Serif", serif`;
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const lines = [];
  let line = '';
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxW && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function drawModuleTitle(ctx, canvas, moduleDisplayName) {
  const trimmed = String(moduleDisplayName || '').trim();
  if (!trimmed) return;

  const maxW = canvas.width * MODULE_MAX_WIDTH_RATIO;
  const baseFont = Math.round(canvas.width * 0.022);
  const minFont = Math.round(canvas.width * 0.016);
  let fontSize = baseFont;
  let lines = wrapLines(ctx, trimmed, fontSize, maxW);
  while (lines.length > 4 && fontSize > minFont) {
    fontSize -= 1;
    lines = wrapLines(ctx, trimmed, fontSize, maxW);
  }

  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#4b2a63';
  ctx.font = `600 ${fontSize}px "Times New Roman", Georgia, "Noto Serif", serif`;

  const lineHeight = Math.round(fontSize * 1.22);
  let y = canvas.height * MODULE_FIRST_BASELINE_Y_RATIO;
  for (const ln of lines) {
    ctx.fillText(ln, canvas.width / 2, y);
    y += lineHeight;
  }
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {HTMLCanvasElement} canvas
 * @param {{ participantName: string, moduleDisplayName: string }} fields
 */
export function paintCertificateDynamicText(ctx, canvas, { participantName, moduleDisplayName }) {
  drawParticipantName(ctx, canvas, participantName);
  drawModuleTitle(ctx, canvas, moduleDisplayName);
}
